import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';

import { useUserDetails } from '../../../store/slices/authSlice';
import { pbxDtmfListGet, usePbxDtmfList } from '../../../store/slices/tenantSlices/settingSlice';

// Static reference of EasyPBX in-call transfer feature codes (IP-phone / softphone,
// no agent GUI). Display only — these codes are handled by the event socket and do NOT
// need rows in easy_pbx_dtmf, so showing them here has no effect on the call flow.
const FEATURE_CODES = [
  { code: '11#<number>', mode: 'Dial', type: 'Manual dial', desc: 'Dial the number directly' },
  { code: '12#<id>', mode: 'Direct', type: 'Direct → Agent', desc: 'Call an agent directly (not on a call).  <id> = Users ID' },
  { code: '13#<id>', mode: 'Direct', type: 'Direct → Process', desc: 'Connect into a process.  <id> = Process ID' },
  { code: '14#<id>', mode: 'Direct', type: 'Direct → IVR', desc: 'Connect into an IVR.  <id> = IVR ID' },
  { code: '15#<id>', mode: 'Direct', type: 'Direct → Queue', desc: 'Pull the next waiting caller from a queue.  <id> = Queue ID' },
  { code: '*22<id>#', mode: 'Blind', type: 'Blind → Agent', desc: 'Transfer caller to an agent.  <id> = Users ID' },
  { code: '*33<id>#', mode: 'Blind', type: 'Blind → Process', desc: 'Transfer caller to a process.  <id> = Process ID' },
  { code: '*44<id>#', mode: 'Blind', type: 'Blind → IVR', desc: 'Transfer caller to an IVR.  <id> = IVR ID' },
  { code: '*88<id>#', mode: 'Blind', type: 'Blind → Queue', desc: 'Transfer caller to a queue.  <id> = Queue ID' },
  { code: '*55<id>#', mode: 'Attended', type: 'Attended → Agent', desc: 'Consult an agent, then complete.  <id> = Users ID' },
  { code: '*66<id>#', mode: 'Attended', type: 'Attended → Process', desc: 'Consult a process, then complete.  <id> = Process ID' },
  { code: '*77<id>#', mode: 'Attended', type: 'Attended → IVR', desc: 'Consult an IVR, then complete.  <id> = IVR ID' },
  { code: '*99<id>#', mode: 'Attended', type: 'Attended → Queue', desc: 'Consult a queue, then complete.  <id> = Queue ID' }
];

// Colour chip per transfer mode so the table scans quickly.
const MODE_STYLE = {
  Dial: { bg: '#e6f4ea', fg: '#0d7a3e' },
  Direct: { bg: '#fde7e3', fg: '#c2410c' },
  Blind: { bg: '#e7effc', fg: '#1d4ed8' },
  Attended: { bg: '#f1e9fb', fg: '#6d28d9' }
};

// Friendly gloss for the single-digit DTMF actions/dispositions stored in easy_pbx_dtmf.
const TYPE_DESC = {
  login: 'Log the agent in',
  logout: 'Log the agent out',
  break_in: 'Start break',
  break_out: 'End break',
  Interested: 'Set disposition: Interested',
  Not_Interested: 'Set disposition: Not Interested',
  CALLBACK: 'Set disposition: Callback',
  PLAY_AGENT_NAME: 'Announce the agent name',
  PLAY_NUMBER: 'Announce the current number',
  MANUAL_DIAL: 'Dial a number directly (11#<number>)',
  REDIAL: 'Redial the last dialled number'
};

// Category palette (same pill treatment as the feature-code Type column).
const CAT_STYLE = {
  session: { bg: '#e7effc', fg: '#1d4ed8' },
  break: { bg: '#fff4e5', fg: '#b45309' },
  disposition: { bg: '#f1e9fb', fg: '#6d28d9' },
  announce: { bg: '#e6f4f4', fg: '#0f766e' },
  dial: { bg: '#e6f4ea', fg: '#0d7a3e' }
};
const DTMF_CAT = {
  login: 'session',
  logout: 'session',
  break_in: 'break',
  break_out: 'break',
  Interested: 'disposition',
  Not_Interested: 'disposition',
  CALLBACK: 'disposition',
  PLAY_AGENT_NAME: 'announce',
  PLAY_NUMBER: 'announce',
  MANUAL_DIAL: 'dial',
  REDIAL: 'dial'
};

const pill = (text, s) => (
  <span
    style={{
      background: s.bg,
      color: s.fg,
      borderRadius: 12,
      padding: '2px 10px',
      fontSize: '0.75rem',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }}>
    {text}
  </span>
);

const codeChip = (txt) => (
  <code
    style={{
      background: '#f1f3f5',
      border: '1px solid #e3e6ea',
      borderRadius: 4,
      padding: '2px 6px',
      color: '#111',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }}>
    {txt}
  </code>
);

const panelTitle = (label) => (
  <div
    className="fw-bold mb-2"
    style={{
      fontSize: '0.8rem',
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      color: '#495057'
    }}>
    {label}
  </div>
);

const PbxDtmf = () => {
  const dispatch = useDispatch();
  const pbxDtmfList = usePbxDtmfList();
  const userDetails = useUserDetails();

  const [isLoading, setIsLoading] = useState(false);
  const handleFormSubmit = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        company_id: userDetails?.companyId
      };
      await dispatch(pbxDtmfListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  useEffect(() => {
    if (userDetails?.companyId) {
      handleFormSubmit();
    }
  }, [userDetails?.companyId]);

  const dtmfRows = pbxDtmfList?.data || [];

  return (
    <div>
      <AdminHeader title="PBX DTMF" isAdd={false} />

      <Card className="p-0 position-relative m-0">
        <div className="p-3 pb-2 d-flex align-items-center justify-content-between flex-wrap">
          <div className="fw-bold">Feature Codes &amp; DTMF</div>
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
            IP-phone / softphone — no agent GUI needed. Requires the agent&apos;s process to have{' '}
            <span className="fw-bold">Easy PBX</span> enabled.
          </div>
        </div>

        {/* Two panels side by side so neither list needs to scroll. */}
        <div className="px-3 pb-2">
          <div className="row g-3">
            {/* ---- Left: single-digit actions & dispositions (from easy_pbx_dtmf) ---- */}
            <div className="col-lg-6">
              {panelTitle('Agent actions & dispositions')}
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th style={{ width: '22%' }}>DTMF</th>
                    <th style={{ width: '33%' }}>Type</th>
                    <th style={{ width: '45%' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={3}>
                        <div className="d-flex justify-content-center py-3">
                          <CallexLoader />
                        </div>
                      </td>
                    </tr>
                  ) : dtmfRows.length > 0 ? (
                    dtmfRows.map((val, index) => (
                      <tr key={`db-${index}`}>
                        <td>{codeChip(val?.dtmf)}</td>
                        <td>
                          {pill(
                            val?.type,
                            CAT_STYLE[DTMF_CAT[val?.type]] || { bg: '#eee', fg: '#333' }
                          )}
                        </td>
                        <td className="text-muted">{TYPE_DESC[val?.type] || '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        No DTMF configured
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ---- Right: in-call transfer feature codes (static) ---- */}
            <div className="col-lg-6">
              {panelTitle('Feature codes (dial & in-call)')}
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th style={{ width: '26%' }}>Code</th>
                    <th style={{ width: '30%' }}>Type</th>
                    <th style={{ width: '44%' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_CODES.map((f, i) => {
                    const s = MODE_STYLE[f.mode] || { bg: '#eee', fg: '#333' };
                    return (
                      <tr key={`fc-${i}`}>
                        <td>{codeChip(f.code)}</td>
                        <td>{pill(f.type, s)}</td>
                        <td className="text-muted">{f.desc}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="p-3 pt-2" style={{ fontSize: '0.8rem' }}>
          <div className="mb-1">
            <span className="fw-bold">Dial codes</span> (when NOT on a call) are dialled as the number:{' '}
            <code>11#&lt;number&gt;</code> manual dial, <code>12#&lt;id&gt;</code>–<code>15#&lt;id&gt;</code>{' '}
            direct connect to agent / process / IVR / queue.
          </div>
          <div className="mb-1">
            <span className="fw-bold">In-call codes</span> (during a live call) are dialled as{' '}
            <code>*&lt;code&gt;&lt;id&gt;#</code> (start with <span className="fw-bold">*</span>, end with{' '}
            <span className="fw-bold">#</span>) — blind <code>*22</code>/<code>*33</code>/<code>*44</code>/
            <code>*88</code>, attended <code>*55</code>/<code>*66</code>/<code>*77</code>/<code>*99</code>.
          </div>
          <div className="mb-1">
            <span className="fw-bold">Example:</span> during a call dial <code>*22495#</code> to
            blind-transfer the caller to the agent whose ID is <span className="fw-bold">495</span>.
            IDs are shown on the Users / Process / IVR pages.
          </div>
          <div>
            <span className="fw-bold">Completing an attended transfer</span> (55 / 66 / 77): after
            dialling the code, consult the target, then press <span className="fw-bold">0</span> =
            conference, <span className="fw-bold">*</span> = complete, <span className="fw-bold">#</span>{' '}
            = cancel (return to the caller).
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PbxDtmf;
